/**
 * CommandDispatcher — the single command execution language (Phase A4).
 *
 * Every shell that can run a typed or clicked command (the 2D command
 * line, the BIM ribbon, the workbench toolbars, plugins, key bindings) goes
 * through here. Resolution order is fixed and mode-agnostic:
 *
 *   1. CAD command parser        (built-in drafting commands + aliases)
 *   2. Command registry          (CommandRegistrar: BIM/NURBS/tool actions,
 *                                 ribbon and toolbar entries)
 *   3. Plugin registry           (custom commands from enabled plugins)
 *
 * The dispatcher returns a uniform result so shells never need to know
 * which layer answered. Unknown input is reported, never silently dropped.
 */

import { parseCommand, getCommandSuggestions, type CommandResult } from '../modules/cad/CommandParser';
import { useCommandManager } from '../state/commandStore';
import { useMacroStore } from '../state/commandAuxStores';
import { usePluginStore } from '../plugins/PluginSystem';
import {
    useCommandPresetStore,
    buildNameIndex,
    effectiveNames,
} from '../state/useCommandPresetStore';
import {
    CANONICAL_COMMANDS,
    normalizeCommandName,
    type CanonicalCommand,
} from './commands/canonicalCommands';
import { runCanonicalCommand } from './commands/commandActions';
import { useAcadPgpStore } from '../state/useAcadPgpStore';
import { listLispCommands, resolveLispCommand, runLispCommand } from './lisp/registry';

export interface DispatchResult {
    success: boolean;
    message: string;
    /** Which resolution layer handled the input (for logging/UI styling). */
    source: 'preset' | 'cad' | 'lisp' | 'registry' | 'plugin' | 'none';
    /** Raw parser result when a shell needs action/params routing. */
    commandResult?: CommandResult;
}

/** Split a raw input line into NAME + ARGS (uppercased name). */
function tokenize(input: string): { name: string; args: string[] } {
    const parts = input.trim().toUpperCase().split(/\s+/);
    return { name: parts[0] ?? '', args: parts.slice(1) };
}

/**
 * Resolve a raw command name through the ACTIVE PRESET's canonical index.
 * Returns null when the preset layer does not claim the name. 'system'
 * commands are intentionally deferred to the legacy parser (its handlers
 * carry the actual logic: undo/zoom/help/...).
 */
export function resolvePresetCommand(input: string): {
    id: string;
    kind: CanonicalCommand['kind'];
    tool?: string;
    actionId?: string;
} | null {
    const state = useCommandPresetStore.getState();
    const preset = state.presets.find((p) => p.id === state.activePresetId);
    if (!preset) return null;
    const index = buildNameIndex(preset);
    const cmd = index.get(normalizeCommandName(tokenize(input).name));
    if (!cmd || cmd.kind === 'system') return null;
    return { id: cmd.id, kind: cmd.kind, tool: cmd.tool, actionId: cmd.actionId };
}

/** Execute one raw command line through the fixed resolution chain. */
export function dispatchCommand(input: string): DispatchResult {
    if (!input.trim()) {
        return { success: false, message: 'Empty command.', source: 'none' };
    }

    // ACTRECORD macro capture: every dispatched line feeds the recorder
    // while it is on, so replay through SCRIPT has real steps.
    if (useMacroStore.getState().recording) {
        useMacroStore.getState().log(input.trim());
    }

    // 0. Preset layer (command-preset-system): canonical actions win when
    //    the active preset maps the typed name. Execution routes through the
    //    shared canonical runner so the full handler chain is honored —
    //    COMMAND_ACTION_HANDLERS first, then the feature-runner registry
    //    (runnerRegistry), with an honest 'not implemented' report when
    //    neither layer claims the action id.
    const presetHit = resolvePresetCommand(input);
    if (presetHit) {
        if (presetHit.kind === 'unbound') {
            return {
                success: false,
                message: `'${presetHit.id}' is registered but not implemented in this build.`,
                source: 'preset',
            };
        }
        const outcome = runCanonicalCommand(presetHit.id, tokenize(input).args);
        return {
            success: outcome.success,
            message: outcome.message,
            source: 'preset',
        };
    }

    // 0.5. AutoLISP c: commands — (defun c:XXX ...) definitions contributed
    // by loaded LISP scripts. Built-in (canonical) names intentionally win;
    // the registry warns at defun time when a c: name shadows a built-in.
    const lispName = tokenize(input).name;
    if (resolveLispCommand(lispName)) {
        runLispCommand(lispName);
        return {
            success: true,
            message: `Running c:${lispName}...`,
            source: 'lisp',
        };
    }

    // 1. Built-in CAD drafting commands (LINE, CIRCLE, UNDO, tool aliases...)
    const parsed = parseCommand(input);
    if (parsed.success) {
        return { ...parsed, source: 'cad', commandResult: parsed };
    }

    // 2. The global command registry (ribbon/toolbar actions registered by
    //    CommandRegistrar). Registry ids are camelCase, so try a direct id
    //    match plus a snake/kebab normalization of the typed name.
    const { name, args } = tokenize(input);
    const registry = useCommandManager.getState();
    // 1.5. acad.pgp extras: aliases whose targets live on the legacy registry
    // (SAVE, OPEN, PASTECLIP...) — outside the preset system, so the pgp
    // import store carries them and they resolve here.
    const pgpExtra = useAcadPgpStore.getState().extras.find((e) => e.alias === name);
    if (pgpExtra && registry.commands[pgpExtra.legacyId]) {
        registry.executeCommand(pgpExtra.legacyId);
        return {
            success: true,
            message: `Executed ${registry.commands[pgpExtra.legacyId].label}.`,
            source: 'registry',
        };
    }
    const normalizedId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const candidate = Object.values(registry.commands).find(
        (cmd) =>
            cmd.id.toUpperCase() === name ||
            cmd.id.toLowerCase() === normalizedId ||
            cmd.label.toUpperCase() === name
    );
    if (candidate) {
        registry.executeCommand(candidate.id);
        return {
            success: true,
            message: `Executed ${candidate.label}${args.length ? ` (${args.join(' ')})` : ''}.`,
            source: 'registry',
        };
    }

    // 3. Plugin custom commands.
    const pluginResult = usePluginStore.getState().executeCommand(name, args);
    if (pluginResult) {
        return { ...pluginResult, source: 'plugin' };
    }

    return {
        success: false,
        message: parsed.message || `Unknown command: ${name}`,
        source: 'none',
    };
}

/**
 * Autocomplete suggestions across all registries (command line support).
 * Preset-aware: active preset primary names lead, legacy CAD commands and
 * their aliases follow with a hint.
 */
export function getDispatchSuggestions(partial: string): string[] {
    const upper = partial.trim().toUpperCase();
    const out: string[] = [];

    if (upper) {
        const state = useCommandPresetStore.getState();
        const preset = state.presets.find((p) => p.id === state.activePresetId);
        if (preset) {
            const seen = new Set<string>();
            for (const cmd of CANONICAL_COMMANDS) {
                // Only primary names lead the list; aliases stay resolvable
                // but don't crowd the (already short) suggestion strip.
                const primary = effectiveNames(preset, cmd)[0];
                if (primary && normalizeCommandName(primary).startsWith(upper) && !seen.has(primary)) {
                    seen.add(primary);
                    out.push(primary);
                }
            }
        }
    }

    // AutoLISP c: commands (user-defined) join the suggestion strip.
    if (upper) {
        for (const name of listLispCommands()) {
            if (name.startsWith(upper) && !out.includes(name)) out.push(name);
        }
    }

    // Legacy parser suggestions (system commands + built-ins), deduplicated.
    for (const s of getCommandSuggestions(partial)) {
        const bare = s.split(' ')[0].toUpperCase();
        if (!out.some((existing) => normalizeCommandName(existing) === bare)) {
            out.push(s);
        }
    }
    return out.slice(0, 12);
}
